import {
  stripPhoneNumberFormatting,
  validateEmail,
  validatePhone,
} from "@/common/utils";

import { createContact } from "./defaults";
import { BulletinContact } from "./types";

/**
 * The self-managing contact list.
 *
 * A pure module rather than a branch inside the section for the same reason as
 * `model/incidentTimes.ts`: these are the rules phase 3's schema has to agree with, and they are far
 * easier to pin down here than through the rendered rows.
 */

/** `id` is client-side, so it is not part of the comparison — an untouched row is an empty one. */
export const isContactRowEmpty = (contact: BulletinContact): boolean =>
  !contact.name &&
  !contact.email &&
  !contact.phoneNumber &&
  !contact.extension &&
  !contact.badgeNumber;

/**
 * Drops every empty row and keeps exactly one blank at the end — `ContactInfoBox`'s behaviour, which
 * is what the design means by "contact rows self-manage like times and locations". Typing into the
 * last row grows the list; emptying a row out removes it.
 *
 * The existing trailing blank is reused rather than replaced. A fresh `createContact()` on every
 * keystroke would change that row's id, and with it its React key, remounting the inputs of a row
 * the officer is about to type into.
 */
export const normalizeContactRows = (
  contacts: BulletinContact[],
): BulletinContact[] => {
  const filled = contacts.filter((contact) => !isContactRowEmpty(contact));
  const last = contacts[contacts.length - 1];

  return [...filled, last && isContactRowEmpty(last) ? last : createContact()];
};

/**
 * Field name → error code. `validation/schemas.ts` and `fields/ContactRow` both read these, and both
 * render them the same way: `t(`validation.contact_${code}`)`.
 */
export type ContactErrorCode =
  | "name_required"
  | "invalid_email"
  | "invalid_phone"
  | "reach_required";

export type ContactErrors = Partial<
  Record<"name" | "email" | "phoneNumber", ContactErrorCode>
>;

/**
 * The contact rules, in one function, so the schema and the row cannot disagree — the same
 * arrangement `validateEntityDraft` has.
 *
 * Three rules, all inherited:
 *
 * 1. **Every row needs a name**, except a trailing all-empty one. That is `validateContactName`
 *    exactly, including its `contacts.length > 1` guard: a lone blank row *is* an error, because it
 *    is the first point of contact.
 * 2. **The first row needs an email or a phone number.** `generateValidationCriteria` reports this
 *    on both `contactEmail` and `contactPhone`; it is one missing answer, so it is reported once,
 *    on the email.
 * 3. **A typed email or phone number has to be valid.** Checked on every row rather than only the
 *    first, which is where this departs from `generateValidationCriteria` — the 2026-07-28 review
 *    asked for "every row validates live", and an invalid address on row 2 reaches the payload just
 *    as the first one does.
 *
 * `validateEmail` and `validatePhone` are production's, per README decision 18 — including
 * `validatePhone` accepting 10 or 11 digits where the prototype accepts only 10.
 */
export const validateContactRow = (
  contact: BulletinContact,
  index: number,
  contacts: BulletinContact[],
): ContactErrors => {
  const errors: ContactErrors = {};

  const exemptTrailingBlank =
    contacts.length > 1 &&
    index === contacts.length - 1 &&
    isContactRowEmpty(contact);

  if (!exemptTrailingBlank && !contact.name.trim()) {
    errors.name = "name_required";
  }

  if (index === 0 && !contact.email.trim() && !contact.phoneNumber.trim()) {
    errors.email = "reach_required";
  } else if (contact.email.trim() && !validateEmail(contact.email.trim())) {
    errors.email = "invalid_email";
  }

  if (
    contact.phoneNumber.trim() &&
    !validatePhone(stripPhoneNumberFormatting(contact.phoneNumber))
  ) {
    errors.phoneNumber = "invalid_phone";
  }

  return errors;
};
